<script>
  import { Tooltip } from "@urbaninstitute/dataviz-components";
  import { SVGMap, SVGPolygonLayer, ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { useData } from "$lib/state/data.svelte.js";
  import { useMapData } from "$lib/state/map.svelte.js";
  import { getName } from "$utils/geoNames.js";
  import { scaleThreshold, scaleLinear, scaleQuantile } from "d3-scale";
  import { jenks } from "simple-statistics";
  import { formatValue, formatShortValue } from "$utils/formats.js";
  import { formatDate } from "$utils/dates.js";

  // props
  let { data, zoomable = true } = $props();

  // consts

  // colors to use for the map
  const MAP_COLORS = [
    urbanColors.blue_shade_lightest,
    urbanColors.blue_shade_light,
    urbanColors.blue,
    urbanColors.blue_shade_dark,
    urbanColors.blue_shade_darkest
  ];

  // state

  // global data state
  let dataState = useData();
  // map data state
  let mapData = useMapData();
  // holds the map container element
  let mapEl = $state();

  /**
   * Tooltip state
   * @type {Object|null}
   * @property {string} title
   * @property {string} date
   * @property {string} value
   * @property {number[]} position
   */
  let mapTooltip = $state(null);

  // derived state

  // are all of the map data layers loaded?
  let mapDataReady = $derived(Promise.all([mapData.stateFeatures, mapData.districtFeatures]));

  // is the map showing districts or states?
  let showDistricts = $derived(dataState.currentLevel === "district");

  // current map feature to highlight
  let activeGeo = $derived(
    dataState.currentLevel === "district" ? dataState.currentDistrict : dataState.currentState
  );

  // types of units for selected metric
  let dataUnits = $derived(dataState.currentMetadata?.units);

  // frequence of selected metric
  let dataFrequency = $derived(dataState.currentMetadata?.frequency);

  // map color scale based on selected data
  let mapColorScale = $derived.by(() => {
    let skipKeys = ["date", "series"];
    let allKeys = Object.keys(data).filter((k) => !skipKeys.includes(k));
    let allValues = allKeys.map((k) => data[k]);
    return getJenksScale(allValues, MAP_COLORS);
    // return scaleThreshold().domain([128400, 357600, 1029600, 3885000]).range(MAP_COLORS);
  });

  /**
   * Sets the geo state based on the geoid.
   * @param {string} geoid - The geographic identifier for the county.
   */
  function setGeo(geoid) {
    if (geoid.length === 2) {
      dataState.currentState = geoid;
    }
    // if its a district, set state and district, just to be safe
    dataState.currentState = geoid.slice(0, 2);
    dataState.currentDistrict = geoid;
  }

  function getGeoColor(geoid) {
    if (Object.hasOwn(data, geoid)) {
      return mapColorScale(data[geoid]);
    }
    return urbanColors.gray_shade_lighter;
  }

  function roundBreak(rawBreak) {
    // Round the breaks
    return rawBreak;
  }

  function handleMousemove(e, props) {
    mapTooltip = {
      title: getName(props.geoid),
      date: formatDate(data.date, dataFrequency),
      value: formatValue(data[props.geoid], dataUnits),
      position: [e.pageX, e.pageY]
    };
  }

  function clearTooltip() {
    mapTooltip = null;
  }

  function handleWindowMousemove(e) {
    if (!mapTooltip || !mapEl) return;
    let mapSVG = mapEl.querySelector(".map-inner-wrapper svg");
    let polygons = mapSVG.querySelectorAll(".polygon-feature");
    let isInPolygon = false;
    for (let polygon of polygons) {
      if (polygon.contains(e.target)) {
        isInPolygon = true;
        break;
      }
    }

    if (!isInPolygon) {
      clearTooltip();
    }
  }

  /**
   * @param {ColorPalette} colorPalette
   * @param {number[]} domain
   * @param {string[]} colors
   * @returns {import("d3-scale").ScaleThreshold<number, string, string>}
   */
  function getJenksScale(domain, colors) {
    if (domain.length === 0) {
      return scaleThreshold().domain([0]).range([urbanColors.gray_shade_lighter]);
    }
    domain.sort();
    const breaks = jenks(domain, colors.length);
    const thresholds = breaks.slice(1, -1).map(roundBreak);
    const result = /** @type {import("d3-scale").ScaleThreshold<number, string, string>} */ (
      scaleThreshold(thresholds, colors)
    );
    return result;
  }
</script>

{#snippet map(features, outlineFeatures)}
  <div class="map-inner-wrapper">
    <SVGMap {features} {zoomable} aspectRatio={4 / 2.3333}>
      <SVGPolygonLayer
        fill={(d) => getGeoColor(d.properties.geoid)}
        on:click={(e) => {
          setGeo(e.detail.props.geoid);
        }}
        strokeWidth={0.5}
        stroke={urbanColors.white}
        hoverStrokeWidth={2}
        hoverStroke={urbanColors.yellow}
        on:mousemove={(e) => {
          handleMousemove(e.detail.e, e.detail.props);
        }}
        on:mouseout={clearTooltip}
        highlightFeature={{ geoid: activeGeo }}
      />
      {#if outlineFeatures}
        <SVGPolygonLayer
          features={outlineFeatures}
          fill="none"
          stroke={urbanColors.white}
          strokeWidth={1}
          pointerEvents={false}
          hoverStroke={urbanColors.yellow}
          hoverStrokeWidth={2}
          highlighFeature={{ geoid: activeGeo }}
        />
      {/if}
    </SVGMap>
  </div>
{/snippet}
<!-- <svelte:window on:mousemove={handleWindowMousemove} /> -->
<div class="map-container" bind:this={mapEl}>
  {#await mapDataReady}
    <div class="loading-container">
      <p>Loading...</p>
    </div>
  {:then [stateFeatures, districtFeatures]}
    {#if mapColorScale}
      <div class="legend-wrap">
        <ColorLegend
          scale={mapColorScale}
          height={16}
          tickFormat={(d) => formatShortValue(d, dataUnits)}
          naFill={urbanColors.gray_shade_lighter}
          naLabel="N/A"
          margin={{ top: 0, right: 10, bottom: 10, left: 0 }}
        />
      </div>
    {/if}
    {#if showDistricts}
      {@render map(districtFeatures.features, stateFeatures.features)}
    {:else}
      {@render map(stateFeatures.features, stateFeatures.features)}
    {/if}
  {/await}
  {#if mapTooltip}
    <Tooltip x={mapTooltip.position[0]} y={mapTooltip.position[1]}>
      <div class="tooltip-title">{mapTooltip.title}</div>
      <div class="tooltip-date">{mapTooltip.date}</div>
      <div class="tooltip-value">{mapTooltip.value}</div>
    </Tooltip>
  {/if}
</div>

<style>
  .map-container {
    position: relative;
    margin-block-start: var(--spacing-8);
  }
  .loading-container {
    background: var(--color-gray-shade-lighter);
    display: flex;
    min-height: 18rem;
    align-items: center;
    justify-content: center;
  }

  .legend-label {
    font-size: var(--font-size-small) !important;
    line-height: 21px !important;
    margin-bottom: var(--spacing-4);
    font-style: italic;
  }

  .tooltip-title,
  .tooltip-value {
    font-size: 14px;
    font-weight: var(--font-weight-bold) !important;
    margin-bottom: var(--spacing-1);
    line-height: 18px;
  }
  .tooltip-date {
    font-size: 14px;
    font-style: italic;
    margin-bottom: var(--spacing-1);
    line-height: 18px;
  }

  @media print {
    .legend-label {
      font-size: 12px;
      line-height: 16px;
      margin-bottom: var(--spacing-2);
    }
  }

  .legend-wrap {
    width: 100%;
    max-width: 350px;
    margin-inline: auto;
  }
  :global(.print-wrap .legend-wrap) {
    @apply mt-4 w-full;
  }
  @media (min-width: 1024px) {
    .map-container {
      padding-bottom: 45px;
    }
  }
</style>
